import { useUser } from "@/api/auth";
import { useDashboardBackgroundImages } from "@/api/dashboard-home";
import { GlobeIcon, UserIcon } from "@/components/home/HomeIcons";
import {
  type NavigationLink,
  NavigationCard,
} from "@/components/home/NavigationCard";
import { ConfigureBlock } from "@/components/home/configure/ConfigureBlock";
import {
  type BackgroundPreview,
  ConfigureDashboardDialog,
} from "@/components/home/configure/ConfigureDashboardDialog";
import { FindArtworkDialog } from "@/components/home/artwork/FindArtworkDialog";
import { glassTheme } from "@/components/home/glass";
import { ManageShortcutsDialog } from "@/components/home/shortcuts/ManageShortcutsDialog";
import { ShortcutsBlock } from "@/components/home/shortcuts/ShortcutsBlock";
import { TutorialsBlock } from "@/components/home/tutorials/TutorialsBlock";
import { TutorialsDialog } from "@/components/home/tutorials/TutorialsDialog";
import NoCMSModel from "@/components/modal/NoCMSModel";
import Header from "@/components/navigation/Header";
import { useAuth } from "@/context/auth/AuthContext";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { imageUrlFor } from "@/lib/dashboard-home/background";
import { resolveBackgroundContrast } from "@/lib/dashboard-home/background-contrast";
import {
  destinationContext,
  type DestinationId,
  resolveDestination,
  route,
} from "@/lib/dashboard-home/destinations";
import {
  CONTACT_LINKS,
  DATA_LINKS,
  type HomeLink,
  WEBSITE_LINKS,
} from "@/lib/dashboard-home/home-links";
import { resolveShortcuts } from "@/lib/dashboard-home/resolve-shortcuts";
import { useMemo, useState } from "react";
import { DataCard } from "./DataCard";
import { HomeBackground } from "./HomeBackground";
import { HomeHero } from "./HomeHero";
import { useHomeNavigation } from "./use-home-navigation";
import { useHomePreferences } from "./use-home-preferences";

/** Référence stable tant que les images personnelles ne sont pas chargées. */
const NO_USER_IMAGES: (string | null)[] = [null, null, null];

/**
 * Page d'accueil du back-office (après le choix de l'instance et au clic sur
 * le logo) : rubriques, raccourcis, tutoriels, et fond choisi par l'utilisateur
 * — les surfaces en verre et le header s'adaptent à ce fond (cf. contrast.ts).
 */
const HomePage = () => {
  const vt = useVisibleT();
  const { instanceData, token, boPreferencesReady } = useAuth();
  const { data: userData } = useUser(token);
  const prefs = useHomePreferences();
  const { go, noCmsOpen, setNoCmsOpen } = useHomeNavigation();

  // Aperçu en direct pendant « Configurer la page ».
  const [preview, setPreview] = useState<BackgroundPreview | null>(null);
  // Une session par ouverture : la modale est remontée (`key`) et son
  // brouillon repart des réglages enregistrés.
  const [configureSession, setConfigureSession] = useState(0);
  const [shortcutsSession, setShortcutsSession] = useState(0);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [tutorials, setTutorials] = useState({ open: false, search: false });
  const [findArtworkOpen, setFindArtworkOpen] = useState(false);

  // Chargées même si un autre fond est actif : vignettes de « Configurer la page ».
  const { data: userImageUrls = NO_USER_IMAGES } =
    useDashboardBackgroundImages(boPreferencesReady);

  const background = preview?.background ?? prefs.background;
  const savedImageUrl =
    prefs.background.type === "image"
      ? imageUrlFor(prefs.background.imageId, userImageUrls)
      : null;
  const imageUrl = preview ? preview.imageUrl : savedImageUrl;
  // Mémo sur la présence de l'image et non sur son URL : l'URL S3 signée
  // change à chaque appel (le dégradé, lui, est mesuré dans un canvas).
  const hasImage = imageUrl !== null;
  const contrast = useMemo(
    () => resolveBackgroundContrast(background, hasImage),
    [background, hasImage],
  );
  const theme = glassTheme(contrast);

  const ctx = useMemo(() => destinationContext(instanceData), [instanceData]);
  const toLinks = (links: HomeLink[]): NavigationLink[] =>
    links.flatMap((link): NavigationLink[] => {
      if (link.id === "findArtwork") {
        return [
          {
            key: link.id,
            label: vt(link.labelKey, link.label),
            onSelect: () => setFindArtworkOpen(true),
          },
        ];
      }
      const destination = resolveDestination(link.id, ctx);
      return destination
        ? [
            {
              key: link.id,
              label: vt(link.labelKey, link.label),
              onSelect: () => go(destination),
            },
          ]
        : [];
    });

  /** Page principale d'une carte ; `undefined` si elle n'existe pas ici. */
  const openFor = (id: DestinationId) => {
    const destination = resolveDestination(id, ctx);
    return destination ? () => go(destination) : undefined;
  };

  const currentUser = userData?.[0];
  const userName = [currentUser?.name, currentUser?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="relative min-h-screen">
      <HomeBackground background={background} imageUrl={imageUrl} />
      <Header
        variant="overlay"
        overlayTone={contrast.headerTone}
        overlayScrim={contrast.headerScrim}
      />

      <main className="relative z-[1] flex flex-col pb-8">
        <HomeHero
          theme={theme}
          userName={userName}
          instanceName={instanceData?.name}
        />

        <div className="flex flex-col gap-4 px-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <DataCard
              links={toLinks(DATA_LINKS)}
              onOpenArtworks={openFor("artworks")}
              showCounts={prefs.showCounts}
              onShowCountsChange={prefs.saveShowCounts}
            />
            <NavigationCard
              icon={<GlobeIcon />}
              title={vt("bo.home.website.title", "Site internet")}
              onTitleSelect={openFor("website")}
              links={toLinks(WEBSITE_LINKS)}
            />
            <NavigationCard
              icon={<UserIcon />}
              title={vt("bo.home.contacts.title", "Contacts")}
              onTitleSelect={openFor("collectors")}
              links={toLinks(CONTACT_LINKS)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,963fr)_minmax(0,464fr)]">
            <ShortcutsBlock
              theme={theme}
              shortcuts={resolveShortcuts(prefs.shortcuts, ctx, vt)}
              onOpen={(shortcut) => go(shortcut.destination)}
              onManage={() => {
                setShortcutsSession((n) => n + 1);
                setShortcutsOpen(true);
              }}
            />
            <div className="flex flex-col gap-4">
              <TutorialsBlock
                theme={theme}
                onOpenList={() => setTutorials({ open: true, search: false })}
                onSearch={() => setTutorials({ open: true, search: true })}
              />
              <ConfigureBlock
                theme={theme}
                onOpen={() => {
                  setConfigureSession((n) => n + 1);
                  setConfigureOpen(true);
                }}
              />
            </div>
          </div>
        </div>
      </main>

      <ConfigureDashboardDialog
        key={`configure-${configureSession}`}
        open={configureOpen}
        onOpenChange={setConfigureOpen}
        background={prefs.background}
        userImageUrls={userImageUrls}
        onPreview={setPreview}
        onSave={prefs.saveBackground}
      />
      <ManageShortcutsDialog
        key={`shortcuts-${shortcutsSession}`}
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
        shortcuts={prefs.shortcuts}
        context={ctx}
        onChange={prefs.saveShortcuts}
        onOpenPath={(path) => go(route(path))}
      />
      <TutorialsDialog
        open={tutorials.open}
        focusSearch={tutorials.search}
        onOpenChange={(open) => setTutorials((t) => ({ ...t, open }))}
      />
      <FindArtworkDialog
        open={findArtworkOpen}
        onOpenChange={setFindArtworkOpen}
      />
      <NoCMSModel open={noCmsOpen} onOpenChange={setNoCmsOpen} />
    </div>
  );
};

export default HomePage;
